import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Ticket } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import SEO from "../components/SEO";
import axios from "axios";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_videocrew-ct/artifacts/ipgpa8tb_VGCT_LOGO.png";

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    user_type: "creative",
    company_name: "",
    promo_code: (params.get("promo") || "").toUpperCase(),
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          user_type: formData.user_type,
          company_name: formData.user_type === "advertiser" ? formData.company_name : null,
          promo_code: formData.promo_code.trim() || null,
        },
        { withCredentials: true }
      );
      
      login(response.data);
      toast.success(response.data.promo_active
        ? "Welcome! Your promo code unlocked the Pro plan for free."
        : "Welcome to Videography Cape Town!");
      navigate(formData.user_type === "advertiser" ? "/advertiser" : "/dashboard");
    } catch (error) {
      toast.error(formatApiError(error, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4" data-testid="register-page">
      <SEO 
        url="/register"
        title="Join the Community"
        description="Create your free profile on Videography Cape Town. Join Cape Town's premier creative directory for videographers, cinematographers, and film professionals."
        keywords={["join videography cape town", "create profile", "film professional directory"]}
      />
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src={LOGO_URL}
              alt="Videography Cape Town"
              className="h-16 mx-auto mb-6"
            />
          </Link>
          <h1 className="font-heading text-3xl uppercase">
            JOIN THE <span className="text-orange-500">COMMUNITY</span>
          </h1>
          <p className="text-zinc-500 mt-2">
            Create your account to get started
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8">
          {/* Google Signup */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignup}
            className="w-full border-zinc-700 text-zinc-300 mb-6"
            data-testid="google-signup-btn"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-zinc-900 text-zinc-500">or</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Account Type */}
            <div>
              <Label className="text-zinc-300 mb-3 block">I am a...</Label>
              <RadioGroup
                value={formData.user_type}
                onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                <label className="flex items-center gap-2 cursor-pointer bg-zinc-800 border border-zinc-700 hover:border-orange-600 px-3 py-2 rounded" data-testid="register-type-creative">
                  <RadioGroupItem value="creative" className="border-zinc-600 text-orange-500" />
                  <span className="text-sm text-zinc-300">Creative Pro</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-zinc-800 border border-zinc-700 hover:border-orange-600 px-3 py-2 rounded" data-testid="register-type-client">
                  <RadioGroupItem value="client" className="border-zinc-600 text-orange-500" />
                  <span className="text-sm text-zinc-300">Client</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-zinc-800 border border-zinc-700 hover:border-orange-600 px-3 py-2 rounded" data-testid="register-type-advertiser">
                  <RadioGroupItem value="advertiser" className="border-zinc-600 text-orange-500" />
                  <span className="text-sm text-zinc-300">Advertiser</span>
                </label>
              </RadioGroup>
              {formData.user_type === "advertiser" && (
                <div className="mt-3">
                  <Label htmlFor="company_name" className="text-zinc-300 text-sm">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your brand / agency"
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    data-testid="company-name-input"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="name" className="text-zinc-300">
                Full Name
              </Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="pl-10 bg-zinc-800 border-zinc-700"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-zinc-300">
                Email
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="pl-10 bg-zinc-800 border-zinc-700"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-zinc-300">
                Password
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-zinc-800 border-zinc-700"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-zinc-300">
                Confirm Password
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10 bg-zinc-800 border-zinc-700"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="promo_code" className="text-zinc-300">
                Promo Code <span className="text-zinc-500 font-normal">(optional)</span>
              </Label>
              <div className="relative mt-2">
                <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  id="promo_code"
                  value={formData.promo_code}
                  onChange={(e) => setFormData({ ...formData, promo_code: e.target.value.toUpperCase() })}
                  placeholder="Have a code? Enter it here"
                  className="pl-10 bg-zinc-800 border-zinc-700 font-mono uppercase"
                  data-testid="promo-code-input"
                />
              </div>
            </div>

            <p className="text-xs text-zinc-500">
              By signing up, you agree to our{" "}
              <Link to="/terms" className="text-orange-500 hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-orange-500 hover:underline">
                Privacy Policy
              </Link>
            </p>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-500"
              data-testid="register-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </div>

        <p className="text-center text-zinc-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-orange-500 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
